<script setup lang="ts">
    import { useRoute, useRouter } from 'vue-router';
    import EchoButton from '../components/EchoButton.vue';
    import SettingsPageLayout from '../components/settings/SettingsPageLayout.vue';
    import { __ } from '../stores/expressions';
    import EditCoachingFormForm from '../components/coaching/EditCoachingFormForm.vue';
    import { useEditCoachingFormStore } from '../stores/edit-coaching-form';
    import { storeToRefs } from 'pinia';
    import {
        useGetCoachingForm,
        useMutateCreateCoachingForm,
    } from '../services/api/coachingForm/coachingFormQueries';
    import { transformCoachingFormDraftToRequest } from '../services/api/coachingForm/utils';
    import { computed, onMounted, provide } from 'vue';
    import { useElementFocus } from '../composables/useElementFocus';
    import { ChevronLeftIcon, XIcon } from '@lucide/vue';
    import { VALIDATION_SCOPE_KEY } from '../composables/useValidationErrors';
    import { COACHING_FORM_VALIDATION_SCOPE } from '../services/api/coachingForm/coachingFormApi';

    provide(VALIDATION_SCOPE_KEY, COACHING_FORM_VALIDATION_SCOPE);

    const { addSection, addQuestion, initiateForm, loadForm } = useEditCoachingFormStore();
    const { formChangeLog, formDraft } = storeToRefs(useEditCoachingFormStore());

    const { setFocus } = useElementFocus();

    const router = useRouter();
    const route = useRoute();

    const { mutateAsync: createCoachingForm, isPending } = useMutateCreateCoachingForm();

    const formId = computed(() => route.params.id as string | null);

    const { data: copyCoachingFormData, refetch: refetchCopyCoachingFormData } =
        useGetCoachingForm(formId);

    const create = async () => {
        if (!formDraft.value) {
            return;
        }

        const form = await createCoachingForm(transformCoachingFormDraftToRequest(formDraft.value));

        router.push('/settings/manage-forms/' + form.id);
    };

    const copyCoachingForm = async () => {
        await refetchCopyCoachingFormData();

        if (copyCoachingFormData.value) {
            const copy = copyCoachingFormData.value;

            loadForm(copy);

            if (formDraft.value) {
                formDraft.value.title = '';
            }
        }
    };

    onMounted(async () => {
        await router.isReady();

        if (route.params.id) {
            await copyCoachingForm();
        } else {
            initiateForm('sv');
            addSection();
            addQuestion(0);
        }

        setFocus('#form-title');
    });
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('settings-manage-forms') }}</h1>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="formChangeLog.length === 0"
                :loading="isPending"
                @click="create()"
            >
                {{ __('edit-coaching-form-save-form') }}
            </EchoButton>

            <EchoButton variant="secondary" route="/settings/manage-forms">
                {{ __('edit-coaching-form-cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <EchoButton size="large" variant="tertiary" route="/settings/manage-forms">
                <template #icon-left><ChevronLeftIcon /></template>
                {{ __('all-forms') }}
            </EchoButton>
            <div class="pt-6">
                <EditCoachingFormForm />
            </div>
        </template>
    </SettingsPageLayout>
</template>
